<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { MAX_PROFILE_FIELDS, uniqueFieldKey } from './fieldMapping';
defineProps({
  disabled: { type: Boolean, default: false },
  paths: { type: Array, default: () => [] },
});
const fields = defineModel('fields', { type: Array, required: true });
const { t } = useI18n();
const types = [
  'text',
  'number',
  'money',
  'datetime',
  'timestamp_seconds',
  'timestamp_milliseconds',
  'status',
  'boolean',
];
const add = () =>
  fields.value.push({
    key: uniqueFieldKey('field', new Set(fields.value.map(field => field.key))),
    label: '',
    path: '',
    type: 'text',
    group: '',
    visible: true,
    currency: 'CNY',
    status_map_text: '{}',
  });
const move = (index, direction) => {
  const [field] = fields.value.splice(index, 1);
  fields.value.splice(index + direction, 0, field);
};
</script>

<template>
  <section class="flex flex-col gap-3 border-t border-n-weak pt-4">
    <div class="flex items-center justify-between gap-2">
      <h3 class="text-heading-3 text-n-slate-12">{{ t('BUSINESS.FIELDS') }}</h3>
      <Button
        type="button"
        :label="t('BUSINESS.ADD_FIELD')"
        :disabled="disabled || fields.length >= MAX_PROFILE_FIELDS"
        @click="add"
      />
    </div>
    <p class="text-sm text-n-slate-11">
      {{ t('BUSINESS.AUTO_FIELDS_HINT', { limit: MAX_PROFILE_FIELDS }) }}
    </p>
    <datalist id="business-response-paths">
      <option v-for="path in paths" :key="path" :value="path" />
    </datalist>
    <fieldset
      v-for="(field, index) in fields"
      :key="field.key"
      :disabled="disabled"
      class="border border-n-weak rounded-lg p-3 grid gap-3 sm:grid-cols-3"
    >
      <label class="text-sm text-n-slate-12 flex flex-col gap-1">
        <span>{{ t('BUSINESS.FIELD_LABEL') }}</span>
        <input
          type="text"
          v-model="field.label"
          class="w-full min-w-0"
          required
          maxlength="80"
        />
      </label>
      <label class="text-sm text-n-slate-12 flex flex-col gap-1">
        <span>{{ t('BUSINESS.FIELD_PATH') }}</span>
        <input
          type="text"
          v-model="field.path"
          class="w-full min-w-0"
          required
          maxlength="200"
          list="business-response-paths"
        />
      </label>
      <label class="text-sm text-n-slate-12 flex flex-col gap-1">
        <span>{{ t('BUSINESS.FIELD_TYPE') }}</span>
        <select v-model="field.type" class="w-full min-w-0">
          <option v-for="type in types" :key="type" :value="type">
            {{ t(`BUSINESS.TYPES.${type}`) }}
          </option>
        </select>
      </label>
      <label class="text-sm text-n-slate-12 flex flex-col gap-1">
        <span>{{ t('BUSINESS.FIELD_GROUP') }}</span>
        <input
          type="text"
          v-model="field.group"
          class="w-full min-w-0"
          maxlength="60"
        />
      </label>
      <label
        v-if="field.type === 'money'"
        class="text-sm text-n-slate-12 flex flex-col"
      >
        <span>{{ t('BUSINESS.CURRENCY') }}</span>
        <input
          type="text"
          v-model="field.currency"
          class="w-full min-w-0"
          required
          pattern="[A-Z]{3}"
          maxlength="3"
        />
      </label>
      <label
        v-if="field.type === 'status'"
        class="sm:col-span-3 flex flex-col gap-1 text-sm text-n-slate-12"
      >
        <span>{{
          t('BUSINESS.STATUS_MAP', {
            example: JSON.stringify({
              1: t('BUSINESS.ENABLED'),
              0: t('BUSINESS.DISABLED'),
            }),
          })
        }}</span>
        <textarea v-model="field.status_map_text" rows="2" maxlength="10000" />
      </label>
      <div
        class="sm:col-span-3 flex flex-wrap items-center justify-between gap-2"
      >
        <label class="text-sm text-n-slate-12 flex items-center gap-2">
          <input v-model="field.visible" type="checkbox" />
          <span>{{ t('BUSINESS.STAFF_VISIBLE') }}</span>
        </label>
        <div class="flex items-center gap-1">
          <Button
            type="button"
            icon="i-lucide-chevron-up"
            :aria-label="t('BUSINESS.MOVE_FIELD_UP')"
            :title="t('BUSINESS.MOVE_FIELD_UP')"
            color="slate"
            variant="ghost"
            :disabled="disabled || index === 0"
            @click="move(index, -1)"
          />
          <Button
            type="button"
            icon="i-lucide-chevron-down"
            :aria-label="t('BUSINESS.MOVE_FIELD_DOWN')"
            :title="t('BUSINESS.MOVE_FIELD_DOWN')"
            color="slate"
            variant="ghost"
            :disabled="disabled || index === fields.length - 1"
            @click="move(index, 1)"
          />
          <Button
            type="button"
            :label="t('BUSINESS.REMOVE_FIELD')"
            color="slate"
            variant="ghost"
            :disabled="disabled || fields.length <= 1"
            @click="fields.splice(index, 1)"
          />
        </div>
      </div>
    </fieldset>
  </section>
</template>
